import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import { map, exhaustMap, catchError, tap } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import {
    signInRequest,
    signInSuccess,
    signInFailure,
    setDataRequest,
    setDataSuccess,
    setDataFailure,
    getUserDataRequest,
    getUserDataSuccess,
    getUserDataFailure,
    logout,
} from '../actions/user.actions';
import { POST_SIGN_IN, GET_USER_DATA } from '../../utils/endpoints';
import { MyToastrService } from '../../shared/services/my-toastr.service';
import { Router } from '@angular/router';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { SIGN_IN_SUCCESS } from '../../utils/messages';

@Injectable()
export class UsersEffects {
    private actions$ = inject(Actions);
    private apiService = inject(ApiService);
    private myToastrService = inject(MyToastrService);
    private localStorageService = inject(LocalStorageService);
    private router = inject(Router);

    signInRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(signInRequest),
            exhaustMap(({ email, password }) =>
                from(this.apiService.post(POST_SIGN_IN, { email, password })).pipe(
                    map((response: any) => {
                        const data = response.data?.data ?? response.data;
                        return signInSuccess({ message: SIGN_IN_SUCCESS, data });
                    }),
                    catchError(error => of(signInFailure({ message: error.response?.data?.message || 'Sign in failed' })))
                )
            )
        )
    );

    signInSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(signInSuccess),
            tap(({ message, data }) => {
                this.myToastrService.showSuccess(message);
                const { refreshTokenInfo, tokenInfo, user } = data;
                this.localStorageService.setItem('refreshTokenInfo', JSON.stringify(refreshTokenInfo));
                this.localStorageService.setItem('tokenInfo', JSON.stringify(tokenInfo));
                this.localStorageService.setItem('user', JSON.stringify(user));
                this.router.navigateByUrl('/auditor');
            })
        ),
        { dispatch: false }
    );

    signInFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(signInFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    setDataRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setDataRequest),
            map(() => {
                const refreshTokenInfo = this.localStorageService.getItem('refreshTokenInfo');
                const tokenInfo = this.localStorageService.getItem('tokenInfo');
                const user = this.localStorageService.getItem('user');

                return setDataSuccess({
                    data: {
                        refreshTokenInfo: JSON.parse(refreshTokenInfo || "{}"),
                        tokenInfo: JSON.parse(tokenInfo || "{}"),
                        user: JSON.parse(user || "{}"),
                    }
                });
            }),
            catchError(() => of(setDataFailure({ message: "" })))
        )
    );

    getUserDataRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getUserDataRequest),
            exhaustMap(() =>
                from(this.apiService.get(GET_USER_DATA)).pipe(
                    map((response: any) => {
                        const { user } = response.data.data;
                        return getUserDataSuccess({ data: user });
                    }),
                    catchError(error => of(getUserDataFailure({ message: error.response?.data?.message || 'Failed to get user data' })))
                )
            )
        )
    );

    getUserDataSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getUserDataSuccess),
            tap(({ data }) => {
                this.localStorageService.setItem('user', JSON.stringify(data));
            })
        ),
        { dispatch: false }
    );

    getUserDataFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getUserDataFailure),
            tap(({ message }) => {
                this.myToastrService.showError(message)
            })
        ),
        { dispatch: false }
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(logout),
            tap(() => {
                this.router.navigateByUrl('/auth/sign-in');
                this.localStorageService.removeItem('refreshTokenInfo');
                this.localStorageService.removeItem('tokenInfo');
                this.localStorageService.removeItem('user');
            })
        ),
        { dispatch: false }
    );
}
